import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft,
  Search,
  BookOpen,
  Users,
  MessageSquare,
  BarChart3,
  Settings,
  Shield,
  FileText,
  Briefcase,
  Bell,
  Database,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Zap,
  Target
} from 'lucide-react';

// Admin Guide Content
const ADMIN_SECTIONS = [
  {
    id: 'overview',
    title: 'Admin Dashboard Overview',
    icon: BarChart3,
    color: 'bg-blue-100 text-blue-700',
    content: `The Admin Dashboard is your central command center for managing the Varyynt platform.

**Dashboard Metrics:**
• **Total Candidates:** Number of registered users on the platform
• **Completed Profiles:** Users who finished all onboarding steps
• **In Progress:** Users with partially completed onboarding
• **New Today:** Users who registered in the last 24 hours

**Charts & Visualizations:**
• **New Signups:** Daily registration trend over the last 30 days
• **Daily Logins:** User activity and engagement over the last 30 days
• **Conversion Funnel:** Tracks users from signup to profile completion

**Quick Actions:**
• **Sync All Users** — Pushes user profiles to the matching database
• **Run Matching Job** — Recalculates V-FIT matches for all users
• **Refresh Status** — Reloads the Job Matching diagnostics panel

**Key Indicators to Watch:**
• Low profile completion rates may indicate onboarding friction
• A drop in daily logins could signal engagement issues
• Zero match results after a job import means you need to run the matching job`
  },
  {
    id: 'candidates',
    title: 'Managing Candidates',
    icon: Users,
    color: 'bg-lime-100 text-lime-700',
    content: `The Candidates section provides full visibility into your user base.

**Candidate List Features:**
• **Search:** Find users by name or email
• **Sort:** Order by registration date, profile completion, or last active
• **Filter by Status:** View all, completed, in-progress, or new users
• **Segment Filtering:** Filter by function, industry, seniority, location type, company size, and more

**Candidate Profile View:**
• View complete profile information including career preferences
• See onboarding completion status for each section
• Review their V-FIT match history and scores
• Access their uploaded resume
• View referral activity and network

**Profile Status Indicators:**
• **Complete (Green):** Profile is fully filled out and matched
• **In Progress (Amber):** Partially completed onboarding
• **New (Gray):** Just registered, hasn't started onboarding

**Best Practices:**
• Monitor new registrations daily
• Follow up with users stuck in onboarding
• Use segment filtering to identify cohorts for targeted messaging`
  },
  {
    id: 'job-import',
    title: 'Job Data Import',
    icon: Database,
    color: 'bg-emerald-100 text-emerald-700',
    content: `The Job Import page allows you to replace all job listings by uploading a CSV file.

**How to Import Jobs:**
1. Navigate to **Admin → Job Import**
2. Review the current database status (job count and sample listings)
3. Click the upload area and select your CSV file
4. Review the preview: total rows, companies, categories, and sample data
5. If the file is valid, click **"Delete & Import Jobs"**
6. Confirm the action in the dialog

**What Happens During Import:**
• All existing **match results** are deleted first (they reference old job IDs)
• All existing **jobs** are deleted
• New jobs from the CSV are inserted in batches
• You'll see a summary with counts for deleted, imported, and final totals

**After Importing:**
• Go to the **Admin Overview** page
• Click **"Run Matching Job"** to generate new V-FIT matches for all users
• This is required — users will see zero matches until you run it

**CSV Format Requirements:**
• Required columns: Job title, Company Name, Description, Apply URL, Date posted, Category name
• Optional columns: Job type, Company URL, Company logo, Job location, Office location, Salary min, Salary maximum, Salary currency
• Date format: MM/DD/YYYY
• The file should be UTF-8 encoded

**Field Normalization:**
• Job type is automatically normalized (e.g., "Full-time" → full_time)
• Location type is derived from the Job location column (remote, hybrid, onsite)
• Salary values are converted to integers

**Important Notes:**
• This action cannot be undone — make sure you have a backup of your CSV
• Large files (8,000+ rows) may take 30-60 seconds to process
• The preview step validates columns without modifying any data`
  },
  {
    id: 'job-matching',
    title: 'Job Matching & V-FIT',
    icon: Target,
    color: 'bg-teal-100 text-teal-700',
    content: `The job matching system uses the V-FIT (Varyynt Future-Intent Trajectory) algorithm to score and rank jobs for each candidate.

**How Matching Works:**
1. Jobs are loaded into the database (via the Job Import page or external pipeline)
2. User profiles are synced from MongoDB to the matching system (Supabase)
3. The matching engine scores every user-job pair using V-FIT
4. Top 20 matches per user (scoring 40+) are stored and shown to candidates

**V-FIT Score Components (0-100):**
• **Skills Alignment (20 pts):** Matches user skills against job description keywords
• **Functional Fit (20 pts):** Compares preferred functions to job category
• **Seniority Trajectory (20 pts):** Checks if job level matches career goals
• **Industry Match (10 pts):** Aligns preferred industries with job industry
• **Company Size (10 pts):** Matches preferred company size bands
• **Compensation Fit (10 pts):** Compares salary expectations with job pay range
• **Location/Work Style (10 pts):** Matches remote/hybrid/onsite preferences and regions

**Match Strength Levels:**
• **Strong (80-100):** Excellent fit across most dimensions
• **Clear (60-79):** Good fit with some alignment gaps
• **Emerging (40-59):** Partial fit, worth exploring

**Resume Enhancement:**
• If a user has uploaded and parsed a resume, the scoring engine uses verified skills, company history, and therapeutic area experience as bonus signals

**Admin Controls (on Overview page):**
• **Sync All Users:** Pushes all completed profiles to the matching database
• **Run Matching Job:** Recalculates V-FIT scores for all users against all jobs
• **Refresh Status:** Reloads diagnostic metrics

**After a Job Import:**
• Match results are automatically cleared during import
• You must click "Run Matching Job" to regenerate matches
• Processing 135 users × 8,000 jobs typically takes 30-60 seconds

**Troubleshooting:**
• **0 Match Results:** Usually means matching hasn't been run since the last job import
• **User reports "No matches":** Ask them to click "Refresh" on their Matches page
• **Poor quality matches:** Review user's preferences — very narrow criteria = fewer matches`
  },
  {
    id: 'job-analytics',
    title: 'Job Market Analytics',
    icon: Briefcase,
    color: 'bg-cyan-100 text-cyan-700',
    content: `The Job Market Analytics page provides visual insights into your job database.

**KPI Cards:**
• **Total Jobs:** Current count of all jobs in the database
• **Companies:** Number of unique employers
• **Categories:** Distinct job function categories
• **Avg Salary:** Average of salary midpoints across all jobs with salary data

**Charts & Visualizations:**
• **Jobs by Category:** Bar chart showing job distribution across function areas
• **Posting Timeline:** Line chart of job postings over time
• **Top Companies:** Horizontal bar chart of companies with the most listings
• **Location Distribution:** Breakdown of remote, hybrid, and onsite jobs

**Data Explorer:**
• Browse all jobs with search, pagination, and sorting
• Click any job to open a detail modal with full description and metadata
• Use "Find Candidates" in the detail modal to see which users have V-FIT matches for that specific job

**Compare with Candidate:**
• Toggle "Show only matched jobs" to filter the job list to only show jobs that a specific candidate has been matched to
• Displays V-FIT scores alongside each matched job
• Useful for verifying match quality or troubleshooting a user's results

**Exporting:**
• Charts can be exported as images using the download buttons`
  },
  {
    id: 'analytics',
    title: 'Platform Analytics',
    icon: BarChart3,
    color: 'bg-violet-100 text-violet-700',
    content: `The Analytics page provides deeper insights into platform health and user behavior.

**Available Analytics:**
• **Signup Trends:** Registration patterns over time with daily/weekly/monthly views
• **Funnel Analysis:** Tracks conversion from signup → verification → onboarding → profile complete
• **User Activity:** Login frequency, session counts, and engagement metrics
• **Profile Completion Breakdown:** Which onboarding steps users get stuck on

**Using Analytics:**
• Use date range filters to zoom into specific periods
• Click on chart data points to drill down into user lists
• Export data for external reporting when needed

**Key Metrics to Monitor:**
• Signup-to-completion conversion rate (healthy: >60%)
• Daily active users vs total users (engagement ratio)
• Average time from signup to profile completion`
  },
  {
    id: 'messaging',
    title: 'Platform Messaging',
    icon: MessageSquare,
    color: 'bg-amber-100 text-amber-700',
    content: `Send targeted or broadcast messages to users through the messaging system.

**Message Types:**
• **Info:** General announcements and updates
• **Warning:** Important notices requiring attention
• **Success:** Positive news and confirmations

**Message Targeting:**
• **All Users:** Broadcast to everyone on the platform
• **Specific User:** Target an individual by email address
• **By Segment:** Filter recipients using multi-criteria segments:
  - Function (e.g., Clinical Development, Commercial)
  - Industry preference (e.g., Pharma, Biotech)
  - Seniority level (e.g., Director, VP)
  - Location type (Remote, Hybrid, Onsite)
  - Company size preference
  - Profile completion status

**Scheduling Options:**
• **Publish Immediately:** Message appears right away
• **Schedule for Later:** Set a future publish date
• **Expiration Date:** Automatically hide after a date

**Managing Messages:**
• View all active and scheduled messages
• Edit message content before publish time
• Use "Instant Expire" to immediately remove a message
• Delete messages that are no longer needed

**Tips:**
• Keep messages concise and actionable
• Use appropriate urgency levels
• Segment-targeted messages have higher engagement than broadcasts
• Don't overuse broadcast messages — save them for important announcements`
  },
  {
    id: 'market-pulse',
    title: 'Market Pulse CMS',
    icon: FileText,
    color: 'bg-red-100 text-red-700',
    content: `Market Pulse is your content management system for industry insights that candidates can read.

**Creating Reports:**
1. Go to Admin → Market Pulse
2. Click "Create New Report"
3. Fill in report details:
   • Title and executive summary
   • Industry category
   • Salary insights (min/max/median)
   • Market trends
   • Full analysis content

**Report Status:**
• **Draft:** Not visible to users, still editing
• **Published:** Live and visible on the candidate Market Pulse page

**Publishing Workflow:**
1. Create report as draft
2. Review and edit content
3. Preview how it will appear
4. Click "Publish" when ready

**Content Guidelines:**
• Keep executive summaries under 200 words
• Include specific salary data when available
• Update reports quarterly at minimum
• Archive outdated reports rather than deleting

**Analytics:**
Track which reports get the most views to guide future content priorities.`
  },
  {
    id: 'user-support',
    title: 'User Support & Account Actions',
    icon: Shield,
    color: 'bg-purple-100 text-purple-700',
    content: `The User Support section provides tools for handling user issues and account management.

**Finding a User:**
1. Go to Admin → User Support
2. Search by email address (exact match required)
3. Click "Search" to load the user's account

**Available Actions:**

**Password Reset:**
• Generates a secure reset link
• Link is valid for 24 hours
• User receives email with reset instructions

**Account Lock/Unlock:**
• Lock: Prevents user from logging in
• Unlock: Restores access
• Use for security concerns or user requests

**Account Suspension:**
• Temporary suspension with reason tracking
• User sees suspension message on login attempt
• Can be reversed at any time

**Account Deletion:**
• Permanently removes user and all their data
• Requires confirmation
• Cannot be undone — use with extreme caution

**Support Notes:**
• Add internal notes to user accounts
• Notes are only visible to admins
• Use for tracking support interactions`
  },
  {
    id: 'support-tickets',
    title: 'Support Tickets',
    icon: Bell,
    color: 'bg-indigo-100 text-indigo-700',
    content: `Handle user support requests through the ticket system.

**Ticket Categories:**
• **General Question:** Basic platform questions
• **Account Issue:** Login, profile, or settings problems
• **Job Matching:** Questions about matches or V-FIT scores
• **Bug Report:** Something isn't working correctly
• **Feature Request:** User suggestions for improvements

**Ticket Workflow:**
1. User submits ticket from their dashboard or support page
2. Ticket appears in Admin → User Support (Support Tickets tab)
3. Admin reviews and responds via email
4. Mark ticket as resolved when complete

**Ticket Fields:**
• Ticket number (auto-generated, e.g., TKT-20260216-001)
• User info (name, email, user ID)
• Category, subject, and message
• Submission timestamp
• Status (Open, In Progress, Resolved, Closed)

**Best Practices:**
• Respond within 24-48 business hours
• Use ticket notes for internal discussion
• Link related tickets for recurring issues
• Escalate security issues immediately`
  },
  {
    id: 'data-security',
    title: 'Data & Security',
    icon: Database,
    color: 'bg-slate-200 text-slate-700',
    content: `Important security and data handling guidelines.

**Access Control:**
• Admin accounts have full platform access
• Admin credentials should never be shared
• Use strong, unique passwords
• Log out when not actively using the admin panel

**Data Architecture:**
• **MongoDB:** Stores user accounts, profiles, preferences, messages, and support tickets
• **Supabase (PostgreSQL):** Stores jobs, match results, and matching job run history
• **AWS S3:** Stores uploaded resumes securely

**User Data:**
• Never share user data externally
• Export user data only when necessary
• All exports are logged for audit purposes
• Follow data retention policies

**Security Incidents:**
If you suspect unauthorized access:
1. Lock affected user accounts immediately
2. Document what you observed
3. Contact technical team
4. Do not communicate with affected users until cleared

**GDPR/Privacy:**
• Users can request data export
• Users can request account deletion
• Honor these requests within 30 days
• Document all data subject requests`
  }
];

// Search helper
const searchSections = (query) => {
  if (!query.trim()) return [];
  const lowerQuery = query.toLowerCase();
  return ADMIN_SECTIONS.filter(section => 
    section.title.toLowerCase().includes(lowerQuery) ||
    section.content.toLowerCase().includes(lowerQuery)
  );
};

// Section View Component
const SectionView = ({ section, onBack }) => (
  <div className="animate-slideUp">
    <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
      <ArrowLeft className="h-4 w-4 mr-2" />
      Back to Guide
    </Button>
    
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${section.color} flex items-center justify-center`}>
            <section.icon className="h-5 w-5" />
          </div>
          <CardTitle>{section.title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="prose prose-slate max-w-none">
          {section.content.split('\n\n').map((paragraph, idx) => {
            if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
              return <h3 key={idx} className="text-lg font-semibold text-slate-900 mt-6 mb-3">{paragraph.replace(/\*\*/g, '')}</h3>;
            }
            if (paragraph.startsWith('•')) {
              const items = paragraph.split('\n').filter(line => line.startsWith('•'));
              return (
                <ul key={idx} className="space-y-2 my-4">
                  {items.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-600">
                      <CheckCircle className="h-4 w-4 text-lime-500 mt-1 flex-shrink-0" />
                      <span dangerouslySetInnerHTML={{ __html: item.substring(2).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                    </li>
                  ))}
                </ul>
              );
            }
            if (/^\d+\./.test(paragraph)) {
              const items = paragraph.split('\n').filter(line => /^\d+\./.test(line));
              return (
                <ol key={idx} className="space-y-2 my-4 list-decimal list-inside">
                  {items.map((item, i) => (
                    <li key={i} className="text-slate-600" dangerouslySetInnerHTML={{ __html: item.replace(/^\d+\.\s*/, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                  ))}
                </ol>
              );
            }
            return <p key={idx} className="text-slate-600 my-4" dangerouslySetInnerHTML={{ __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong class="text-slate-900">$1</strong>') }} />;
          })}
        </div>
      </CardContent>
    </Card>
  </div>
);

export default function AdminGuidePage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSection, setSelectedSection] = useState(null);

  const searchResults = searchSections(searchQuery);
  const displaySections = searchQuery ? searchResults : ADMIN_SECTIONS;

  if (selectedSection) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-evergreen-950 text-white sticky top-0 z-40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/admin" className="flex items-center gap-2">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10 brightness-0 invert" />
                <Badge variant="outline" className="border-white/30 text-white text-xs">Admin</Badge>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10">
                Sign Out
              </Button>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <SectionView section={selectedSection} onBack={() => setSelectedSection(null)} />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-evergreen-950 text-white sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-white hover:bg-white/10">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="h-6 w-px bg-white/20" />
              <Link to="/admin" className="flex items-center gap-2">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10 brightness-0 invert" />
                <Badge variant="outline" className="border-white/30 text-white text-xs">Admin</Badge>
              </Link>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10">
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-br from-evergreen-950 via-evergreen-900 to-evergreen-950 text-white pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-lime-500/20 mb-6">
              <BookOpen className="h-8 w-8 text-lime-400" />
            </div>
            <h1 className="text-3xl font-heading mb-3">Admin User Guide</h1>
            <p className="text-white/70 max-w-xl mx-auto mb-8">
              Complete documentation for managing the Varyynt platform
            </p>
            
            {/* Search */}
            <div className="relative max-w-lg mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Search documentation..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 bg-white text-slate-900"
                data-testid="admin-guide-search"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 -mt-6">
        {searchQuery && displaySections.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No results found</h3>
              <p className="text-slate-500">Try different keywords or browse the sections below.</p>
              <Button variant="outline" onClick={() => setSearchQuery('')} className="mt-4">
                Clear search
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {displaySections.map((section) => {
              const Icon = section.icon;
              // Strip markdown from preview
              const cleanPreview = section.content
                .replace(/\*\*(.*?)\*\*/g, '$1')
                .replace(/\n/g, ' ')
                .replace(/•/g, '')
                .replace(/\s+/g, ' ')
                .trim()
                .substring(0, 150);
              return (
                <Card 
                  key={section.id}
                  className="hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => setSelectedSection(section)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-lg ${section.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 group-hover:text-lime-700 transition-colors">
                          {section.title}
                        </h3>
                        <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                          {cleanPreview}...
                        </p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-300 group-hover:text-lime-500 transition-colors flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Quick Links */}
        <Card className="mt-8 border-lime-200 bg-gradient-to-br from-lime-50 to-emerald-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Zap className="h-5 w-5 text-lime-600" />
              Quick Links
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Candidates', href: '/admin/candidates', icon: Users },
                { label: 'Job Market', href: '/admin/job-analytics', icon: Briefcase },
                { label: 'Job Import', href: '/admin/job-import', icon: Database },
                { label: 'Messages', href: '/admin/messages', icon: MessageSquare },
                { label: 'User Support', href: '/admin/support', icon: Shield },
                { label: 'Market Pulse', href: '/admin/market-pulse', icon: FileText },
                { label: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
              ].map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center gap-2 p-3 rounded-lg bg-white border border-slate-200 hover:border-lime-300 hover:bg-lime-50 transition-all text-sm font-medium text-slate-700 hover:text-lime-700"
                >
                  <link.icon className="h-4 w-4" />
                  {link.label}
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
